/**
 * Anillo de progreso coral (design-system §8.1 · flujo de marcación): trazo de 4 px sobre una
 * pista tenue.
 *
 * - Sin `progress`: **indeterminado** ("Buscando marcador…"): arco del 25 % girando 1.6 s lineal
 *   (`.fm-spin`). Con `prefers-reduced-motion` el anillo se dibuja completo y no gira.
 * - Con `progress` 0–100: **determinado** ("Verificando…"): el arco se completa siguiendo el
 *   avance real, con transición `duration-xslow`.
 *
 * El estado nunca se comunica solo por el anillo (§10): `label` es obligatorio y el texto visible
 * lo pone la pantalla en una región `aria-live`.
 */
import { cn } from './cn';

export interface SpinnerRingProps {
  /** Texto accesible del progreso (p. ej. "Buscando marcador…"). */
  label: string;
  /** 0–100; si se omite, el anillo es indeterminado. */
  progress?: number;
  /** Lado en px (48 por defecto). */
  size?: number;
  strokeWidth?: number;
  className?: string;
}

const VIEWBOX = 48;

export function SpinnerRing({
  label,
  progress,
  size = 48,
  strokeWidth = 4,
  className,
}: SpinnerRingProps) {
  const determinate = typeof progress === 'number' && Number.isFinite(progress);
  const clamped = determinate ? Math.max(0, Math.min(100, progress)) : 0;
  const radius = (VIEWBOX - strokeWidth) / 2;
  const center = VIEWBOX / 2;

  return (
    <svg
      role="progressbar"
      aria-label={label}
      aria-valuemin={determinate ? 0 : undefined}
      aria-valuemax={determinate ? 100 : undefined}
      aria-valuenow={determinate ? Math.round(clamped) : undefined}
      data-state={determinate ? 'determinate' : 'indeterminate'}
      width={size}
      height={size}
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      className={cn('shrink-0', !determinate && 'fm-spin', className)}
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        className="stroke-fm-bg-sunken"
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        pathLength={100}
        transform={`rotate(-90 ${center} ${center})`}
        strokeDasharray={determinate ? `${clamped} ${100 - clamped}` : undefined}
        className={cn(
          'stroke-fm-primary',
          determinate
            ? 'transition-[stroke-dasharray] duration-fm-xslow ease-fm-standard motion-reduce:transition-none'
            : 'fm-ring-indeterminate',
        )}
      />
    </svg>
  );
}
